import { useState, useEffect, useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '';

/**
 * Quick Rework Modal - v2.1
 *
 * Fast, touch-friendly modal for reporting rework issues.
 * Target: < 15 seconds to submit.
 *
 * Props:
 * - isOpen: boolean - Whether modal is visible
 * - onClose: () => void - Close modal callback
 * - onSubmit: (data) => void - Submit callback
 * - context?: { jobNumber, quoteNumber, orderNumber, projectName, workCenter, skus }
 * - token?: string - Auth token for API calls
 */
export default function QuickReworkModal({ isOpen, onClose, onSubmit, context = {}, token }) {
  const [issueTypes, setIssueTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // SKU list state
  const [skuList, setSkuList] = useState([]);
  const [loadingSkus, setLoadingSkus] = useState(false);
  const [skuSearch, setSkuSearch] = useState('');

  // Form state
  const [selectedJob, setSelectedJob] = useState(context?.jobNumber || '');
  const [selectedSku, setSelectedSku] = useState(null); // { jobNumber, sku, description }
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [severity, setSeverity] = useState('requires_rework');
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);

  // Fetch SKUs for project when quoteNumber is provided
  const fetchSkus = useCallback(async (quoteNumber) => {
    if (!quoteNumber) return;
    setLoadingSkus(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_BASE}/api/v2/production/projects/${encodeURIComponent(quoteNumber)}/jobs`, { headers });
      if (!res.ok) throw new Error('Failed to fetch SKUs');
      const data = await res.json();
      setSkuList(data.jobs || []);
    } catch (err) {
      console.error('Error fetching SKUs:', err);
      setSkuList([]);
    } finally {
      setLoadingSkus(false);
    }
  }, [token]);

  // Reset form when opened
  useEffect(() => {
    if (isOpen) {
      setSelectedJob(context?.jobNumber || '');
      setSelectedSku(null);
      setSelectedIssue(null);
      setSeverity('requires_rework');
      setNotes('');
      setShowNotes(false);
      setError(null);
      setSkuSearch('');

      // Use provided SKUs or fetch from API
      if (context?.skus?.length > 0) {
        setSkuList(context.skus);
      } else if (context?.quoteNumber) {
        fetchSkus(context.quoteNumber);
      } else {
        setSkuList([]);
      }

      // Pre-select if jobNumber is provided
      if (context?.jobNumber && context?.skus) {
        const foundSku = context.skus.find(s => s.jobNumber === context.jobNumber);
        if (foundSku) setSelectedSku(foundSku);
      }
    }
  }, [isOpen, context?.jobNumber, context?.quoteNumber, context?.skus, fetchSkus]);

  // Fetch issue types on mount
  useEffect(() => {
    async function fetchIssueTypes() {
      try {
        const res = await fetch('/api/rework-issue-types');
        if (!res.ok) throw new Error('Failed to fetch issue types');
        const data = await res.json();
        setIssueTypes(data.issueTypes || []);
      } catch (err) {
        console.error('Error fetching issue types:', err);
        // Fallback issue types if API fails
        setIssueTypes([
          { code: 'pa_wrong_file', label: 'Wrong File', category: 'human' },
          { code: 'print_headstrike', label: 'Head Strike', category: 'equipment' },
          { code: 'print_material', label: 'Material Defect', category: 'material' },
          { code: 'asm_damage', label: 'Damage', category: 'human' },
          { code: 'print_banding', label: 'Banding', category: 'equipment' },
          { code: 'other', label: 'Other', category: 'other' },
        ]);
      }
    }
    fetchIssueTypes();
  }, []);

  // Quick issue buttons - most common issues
  const quickIssues = issueTypes.slice(0, 6);

  // Filter SKUs by search
  const filteredSkus = skuList.filter(sku => {
    if (!skuSearch) return true;
    const search = skuSearch.toLowerCase();
    return (
      sku.jobNumber?.toLowerCase().includes(search) ||
      sku.sku?.toLowerCase().includes(search) ||
      sku.description?.toLowerCase().includes(search)
    );
  });

  // Get effective job number (from SKU selection or manual entry)
  const effectiveJobNumber = selectedSku?.jobNumber || selectedJob;

  const handleSubmit = async () => {
    if (!effectiveJobNumber || !selectedIssue) {
      setError('Please select a SKU and issue type');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        jobNumber: effectiveJobNumber,
        issueType: selectedIssue.code,
        severity,
        discoveredWorkCenter: context?.workCenter || 'OPTI',
        quoteNumber: context?.quoteNumber || null,
        orderNumber: context?.orderNumber || null,
        projectName: context?.projectName || null,
        description: notes || null,
        reportedById: localStorage.getItem('userId') || 'unknown',
        reportedByName: localStorage.getItem('userName') || 'Unknown',
      };

      const res = await fetch('/api/rework-events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to create report');
      }

      const result = await res.json();

      if (onSubmit) {
        onSubmit(result);
      }

      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Report Issue</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* SKU/Job Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SKU / Job
              {loadingSkus && <span className="ml-2 text-gray-400 text-xs">Loading...</span>}
            </label>

            {/* Show dropdown if SKUs available, otherwise show text input */}
            {skuList.length > 0 ? (
              <div className="space-y-2">
                {/* Search filter for large lists */}
                {skuList.length > 5 && (
                  <input
                    type="text"
                    value={skuSearch}
                    onChange={(e) => setSkuSearch(e.target.value)}
                    placeholder="Search SKUs..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                )}

                {/* SKU List */}
                <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {filteredSkus.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">No SKUs match your search</div>
                  ) : (
                    filteredSkus.map((sku) => (
                      <button
                        key={sku.jobNumber}
                        onClick={() => {
                          setSelectedSku(sku);
                          setSelectedJob(sku.jobNumber);
                        }}
                        className={`w-full text-left px-3 py-2.5 transition-colors ${
                          selectedSku?.jobNumber === sku.jobNumber
                            ? 'bg-blue-50 border-l-4 border-blue-500'
                            : 'hover:bg-gray-50 border-l-4 border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-sm text-blue-600 font-medium">{sku.jobNumber}</span>
                          <span className="text-xs text-gray-500">Qty: {sku.qty?.toLocaleString() || 0}</span>
                        </div>
                        <div className="text-xs text-gray-600 truncate mt-0.5">
                          {sku.sku && <span className="mr-2 text-gray-500">{sku.sku}</span>}
                          {sku.description}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : (
              /* Manual entry fallback */
              <input
                type="text"
                value={selectedJob}
                onChange={(e) => setSelectedJob(e.target.value)}
                placeholder="e.g., 2001234-01"
                className="w-full px-3 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!!context?.jobNumber}
              />
            )}

            {/* Selected SKU display */}
            {selectedSku && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-blue-700">Selected: {selectedSku.jobNumber}</span>
                  {selectedSku.description && (
                    <p className="text-xs text-blue-600 truncate">{selectedSku.description}</p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedSku(null);
                    setSelectedJob('');
                  }}
                  className="text-blue-500 hover:text-blue-700 p-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {context?.projectName && (
              <p className="mt-1 text-sm text-gray-500">{context.projectName}</p>
            )}
          </div>

          {/* Issue Type Buttons */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">What went wrong?</label>
            <div className="grid grid-cols-3 gap-2">
              {quickIssues.map((issue) => (
                <button
                  key={issue.code}
                  onClick={() => setSelectedIssue(issue)}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors min-h-[48px] ${
                    selectedIssue?.code === issue.code
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {issue.label}
                </button>
              ))}
            </div>
            {/* More issues dropdown */}
            {issueTypes.length > 6 && (
              <select
                value={selectedIssue?.code || ''}
                onChange={(e) => {
                  const issue = issueTypes.find((i) => i.code === e.target.value);
                  setSelectedIssue(issue || null);
                }}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">More options...</option>
                {issueTypes.slice(6).map((issue) => (
                  <option key={issue.code} value={issue.code}>
                    {issue.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Severity Buttons */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">How bad is it?</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setSeverity('cosmetic')}
                className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                  severity === 'cosmetic'
                    ? 'border-gray-400 bg-gray-100 text-gray-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                Minor / Cosmetic
              </button>
              <button
                onClick={() => setSeverity('requires_rework')}
                className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                  severity === 'requires_rework'
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                Requires Rework
              </button>
              <button
                onClick={() => setSeverity('scrap')}
                className={`p-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                  severity === 'scrap'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                }`}
              >
                SCRAP
              </button>
            </div>
          </div>

          {/* Notes (collapsible) */}
          <div>
            <button
              onClick={() => setShowNotes(!showNotes)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {showNotes ? '- Hide notes' : '+ Add notes (optional)'}
            </button>
            {showNotes && (
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Describe the issue..."
                rows={3}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleSubmit}
            disabled={loading || !effectiveJobNumber || !selectedIssue}
            className={`w-full py-4 rounded-lg font-semibold text-lg transition-colors ${
              loading || !effectiveJobNumber || !selectedIssue
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
            }`}
          >
            {loading ? 'Submitting...' : 'SUBMIT REPORT'}
          </button>
        </div>
      </div>
    </div>
  );
}
